<template>
  <div class="flex w-full flex-col items-center self-stretch bg-light-bg-inset p-4">
    <div class="flex w-full flex-col items-start gap-4 py-2">
      <div class="flex w-full flex-col items-end gap-2">
        <CurrencySelector :disabled="props.readonly" />
        <!-- ゲストタイプ料金表示 -->
        <div
          v-for="guestTypePrice in guestTypePriceDisplays"
          :key="guestTypePrice.id"
          class="inline-flex items-center justify-start gap-2 self-stretch"
          :data-testid="`guest-type-price-${guestTypePrice.name.toLowerCase()}`"
        >
          <div class="flex-1 justify-start font-Spectral text-sm font-light leading-tight text-light-fg-muted">
            {{ guestTypePrice.count }} {{ guestTypePrice.name }}: ({{ guestTypePrice.unitPriceDisplay }})
          </div>
          <div class="justify-start text-right font-Spectral text-sm font-normal leading-tight text-light-fg-muted">
            {{ guestTypePrice.totalPriceDisplay }}
          </div>
        </div>

        <!-- 通訳オプション料金表示 -->
        <div v-if="hasInterpreterOption" class="inline-flex items-end justify-start gap-2 self-stretch">
          <div class="flex-1 justify-start font-Spectral text-sm font-light leading-tight text-light-fg-muted">Interpretation in {{ guestTypeInterpreterOption.name }}</div>
          <div class="justify-start text-right font-Spectral text-sm font-normal leading-tight text-light-fg-muted">
            {{ interpreterOptionDisplay }}
          </div>
        </div>

        <!-- その他オプション料金表示 -->
        <div v-for="(option, index) in guestTypeOptionsDisplay" :key="`option-${index}`" class="inline-flex items-end justify-start gap-2 self-stretch">
          <div class="flex-1 justify-start font-Spectral text-sm font-light leading-tight text-light-fg-muted">{{ option.name }} ({{ option.unitPriceDisplay }} × {{ option.count }})</div>
          <div class="justify-start text-right font-Spectral text-sm font-normal leading-tight text-light-fg-muted">
            {{ option.totalPriceDisplay }}
          </div>
        </div>

        <!-- 会計処理方式別の内訳表示（コード適用時） -->
        <template v-if="calculationMethod">
          <!-- ① 区切り線: 購入品目と諸計算項目の間 -->
          <div class="w-full border-t border-light-bd-muted" />

          <!-- 割引前小計（税込） -->
          <div class="inline-flex items-end justify-start gap-2 self-stretch">
            <div class="flex-1 justify-start font-Spectral text-sm font-light leading-tight text-light-fg-muted">Subtotal (incl. tax)</div>
            <div class="justify-start text-right font-Spectral text-sm font-normal leading-tight text-light-fg-muted" data-testid="subtotalBeforeDiscount">{{ subtotalBeforeDiscountDisplay }}</div>
          </div>

          <!-- 税抜ベース割引（%割引コード） -->
          <TaxExcludedDiscountBreakdown
            v-if="calculationMethod === 'TAX_EXCLUDED_DISCOUNT'"
            :benefit-value="appliedCode?.benefitValue"
            :discount-display="discountDisplay"
            :pre-tax-subtotal-display="preTaxSubtotalDisplay"
            :tax-amount-display="taxAmountDisplay"
            :taxable-amount-display="taxableAmountDisplay"
          />

          <!-- コミッション（コミッションコード） -->
          <CommissionBreakdown v-else-if="calculationMethod === 'COMMISSION'" :benefit-value="appliedCode?.benefitValue" :commission-display="discountDisplay" />

          <!-- 税込ベース割引（定額割引コード） -->
          <TaxIncludedDiscountBreakdown v-else-if="calculationMethod === 'TAX_INCLUDED_DISCOUNT'" :discount-display="discountDisplay" />
        </template>

        <!-- 合計の手前に差し込む行（新動線の特典コード欄など） -->
        <div v-if="$slots.beforeTotal" class="self-stretch">
          <slot name="beforeTotal" />
        </div>

        <!-- ② 区切り線: 諸計算項目と合計（Total）の間 -->
        <div v-if="calculationMethod" class="w-full border-t border-dashed border-light-bd-muted" />

        <!-- 小計/合計表示 -->
        <div class="flex flex-col items-start justify-end self-stretch">
          <div class="inline-flex items-end justify-between self-stretch">
            <p class="flex-1 font-Spectral text-base font-normal leading-6 text-light-fg-default">Total ({{ displayCurrency }})</p>
            <p class="text-right font-Spectral text-xl font-medium capitalize leading-7 tracking-[0.5px] text-light-fg-default" data-testid="totalPrice">
              {{ totalDisplay }}
            </p>
          </div>
          <!-- 通常モード: 外貨選択時にJPY税込を表示 -->
          <div v-if="!props.readonly && displayCurrency !== 'JPY'" class="w-full text-right font-Spectral text-[11px] leading-tight text-light-fg-muted md:text-[13px]">
            {{ formattedTotalPriceIncludingTax }} (tax incl.)
          </div>
          <!-- readonlyモード: 選択通貨がJPY以外の場合にApprox表示 -->
          <p v-if="props.readonly && selectedCurrency !== 'JPY'" class="w-full text-right font-Spectral text-sm font-normal leading-5 text-light-fg-muted">Approx. {{ approxDisplay }} (tax incl.)</p>
        </div>
        <!-- 注釈 -->
        <div class="font-Spectral text-sm font-normal leading-5 text-light-fg-muted">
          <p v-if="showCurrencyNote" class="mb-0.5">
            * Payments and refunds are processed in Japanese Yen. The final amount in your currency may vary slightly depending on the exchange rate on the day of processing.
          </p>
          <p>* Prices may vary depending on options selected.</p>
          <p v-if="calculationMethod === 'TAX_EXCLUDED_DISCOUNT'" class="mt-0.5">* Discount is calculated on the pre-tax amount.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import CurrencySelector from '@/wabunka-src/common/components/stateful/CurrencySelector/CurrencySelector.vue';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import CommissionBreakdown from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/CommissionBreakdown.vue';
import TaxExcludedDiscountBreakdown from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TaxExcludedDiscountBreakdown.vue';
import TaxIncludedDiscountBreakdown from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TaxIncludedDiscountBreakdown.vue';
import type { WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import { type WabunkaCode, wabunkaCodeCalculationMethod } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaCode';
import type {
  ExperiencePreReservationPriceWithGuestTypeViewModel,
  ReservationGuestTypePriceSet,
  ReservationExperienceEventOptionPriceSet,
  ReservationExperienceEventInterpreterOptionPriceSet,
} from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';
import { parsePriceString, createDetailedPriceInfo, formatTotalPriceDisplay } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';

interface Props {
  defaultExperience?: WabunkaExperienceViewModel;
  experienceEvent: WabunkaExperienceEventViewModel;
  preReservationPriceWithGuestType?: ExperiencePreReservationPriceWithGuestTypeViewModel;
  selectedExperienceEvent?: WabunkaExperienceEventViewModel;
  appliedCode?: WabunkaCode;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
});
const { selectedCurrency } = useCurrency();

// readonlyモードでは常にJPY表示、通常モードでは選択通貨で表示
const displayCurrency = computed(() => (props.readonly ? 'JPY' : selectedCurrency.value));

const priceData = computed(() => props.preReservationPriceWithGuestType);

const guestTypePrices = computed(() => {
  if (!priceData.value) return [];

  return priceData.value.guestTypePriceSets.map((set: ReservationGuestTypePriceSet) => {
    return createDetailedPriceInfo(set);
  });
});

const guestTypePriceDisplays = computed(() =>
  guestTypePrices.value.map((p, index) => {
    if (displayCurrency.value === 'JPY') {
      return { ...p, unitPriceDisplay: p.formattedUnitPrice, totalPriceDisplay: p.formattedTotalPrice };
    }
    const guestTypeSet = priceData.value?.guestTypePriceSets[index];
    const totalPriceDisplay = guestTypeSet?.currencyConvertedPrice?.formattedAmount ?? '...';
    const unitPriceDisplay = p.count > 0 && guestTypeSet?.currencyConvertedPrice ? guestTypeSet.currencyConvertedPrice.formattedAmount : '...';
    return { ...p, unitPriceDisplay, totalPriceDisplay };
  }),
);

// オプション料金
const guestTypeOptions = computed(() => {
  if (!priceData.value) return [];

  return priceData.value.experienceEventOptionPriceSets
    .filter((option: ReservationExperienceEventOptionPriceSet) => option.count > 0)
    .map((option: ReservationExperienceEventOptionPriceSet) => ({
      ...option,
      formattedUnitPrice: `¥${parsePriceString(option.unitPriceIncludingTaxString).toLocaleString()}`,
      formattedTotalPrice: `¥${parsePriceString(option.totalPriceIncludingTaxString).toLocaleString()}`,
      currencyConvertedUnitPrice: option.currencyConvertedUnitPrice,
      currencyConvertedTotalPrice: option.currencyConvertedTotalPrice,
    }));
});

const guestTypeOptionsDisplay = computed(() =>
  guestTypeOptions.value.map((option) => ({
    name: option.name,
    count: option.count,
    unitPriceDisplay: displayCurrency.value === 'JPY' ? option.formattedUnitPrice : (option.currencyConvertedUnitPrice?.formattedAmount ?? '...'),
    totalPriceDisplay: displayCurrency.value === 'JPY' ? option.formattedTotalPrice : (option.currencyConvertedTotalPrice?.formattedAmount ?? '...'),
  })),
);

const hasInterpreterOption = computed(() => {
  if (!priceData.value) return false;
  return priceData.value.interpreterOptionPriceSets.some((option: ReservationExperienceEventInterpreterOptionPriceSet) => option.count > 0);
});

const guestTypeInterpreterOption = computed(() => {
  if (!priceData.value) {
    return { name: '', price: 0, formattedPrice: '¥0', currencyConvertedUnitPrice: undefined };
  }

  const option = priceData.value.interpreterOptionPriceSets.find((option: ReservationExperienceEventInterpreterOptionPriceSet) => option.count > 0);
  if (!option) return { name: '', price: 0, formattedPrice: '¥0', currencyConvertedUnitPrice: undefined };

  const price = parsePriceString(option.unitPriceIncludingTaxString);
  return {
    name: option.name,
    price: price,
    formattedPrice: `¥${price.toLocaleString()}`,
    currencyConvertedUnitPrice: option.currencyConvertedUnitPrice,
  };
});

const interpreterOptionDisplay = computed(() => {
  if (displayCurrency.value === 'JPY') return guestTypeInterpreterOption.value.formattedPrice;
  return guestTypeInterpreterOption.value.currencyConvertedUnitPrice?.formattedAmount ?? '...';
});

const totalPriceIncludingTax = computed(() => {
  if (!priceData.value) return 0;
  return priceData.value.totalPriceIncludingTax;
});

const formattedTotalPriceIncludingTax = computed(() => {
  return `¥${totalPriceIncludingTax.value.toLocaleString()}`;
});

const discountAmount = computed(() => {
  if (!priceData.value) return 0;

  return priceData.value.discountAmountString ? parsePriceString(priceData.value.discountAmountString) : 0;
});

const discountDisplay = computed(() => {
  if (displayCurrency.value === 'JPY') return `¥${discountAmount.value.toLocaleString()}`;
  return priceData.value?.currencyConvertedDiscountAmount?.formattedAmount ?? '...';
});

const totalDisplay = computed(() => formatTotalPriceDisplay(priceData.value, displayCurrency.value));

// 会計処理方式。適用されたコードの benefitType と 1:1 で対応する（モデル側で判定）
const calculationMethod = computed(() => wabunkaCodeCalculationMethod(props.appliedCode));

const subtotalBeforeDiscountDisplay = computed(() => {
  const data = priceData.value;
  if (!data) return '...';
  if (displayCurrency.value === 'JPY') return `¥${(data.totalPriceIncludingTaxBeforeDiscount ?? data.totalPriceIncludingTax).toLocaleString()}`;
  return data.currencyConvertedTotalPriceIncludingTaxBeforeDiscount?.formattedAmount ?? '...';
});

const preTaxSubtotalDisplay = computed(() => {
  if (displayCurrency.value === 'JPY') return `¥${priceData.value?.totalPriceExcludingTaxBeforeDiscountString ?? '0'}`;
  return priceData.value?.currencyConvertedTotalPriceExcludingTaxBeforeDiscount?.formattedAmount ?? '...';
});

const taxableAmountDisplay = computed(() => {
  if (displayCurrency.value === 'JPY') return `¥${priceData.value?.totalPriceExcludingTaxString ?? '0'}`;
  return priceData.value?.currencyConvertedTotalPriceExcludingTax?.formattedAmount ?? '...';
});

const taxAmountDisplay = computed(() => {
  if (displayCurrency.value === 'JPY') return `¥${priceData.value?.taxAmountString ?? '0'}`;
  return priceData.value?.currencyConvertedTaxAmount?.formattedAmount ?? '...';
});

// readonlyモードで選択通貨がJPY以外の場合のApprox表示
const approxDisplay = computed(() => {
  return priceData.value?.currencyConvertedPrice?.formattedAmount ?? '...';
});

// 通貨注釈の表示条件
const showCurrencyNote = computed(() => {
  if (props.readonly) return selectedCurrency.value !== 'JPY';
  return displayCurrency.value !== 'JPY';
});
</script>
